import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ListItemIcon,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import { MenuListLink } from './MenuListLink';
import { SidebarLink, SidebarSection as SidebarSectionType } from './utils';

interface SidebarSectionProps {
  section: SidebarSectionType;
  visibleChildren: SidebarLink[];
  activeChildPath: string | null;
  onLinkClick: () => void;
}

export const SidebarSection = ({
  section,
  visibleChildren,
  activeChildPath,
  onLinkClick,
}: SidebarSectionProps) => {
  const theme = useTheme();
  const hasActiveChild = activeChildPath !== null;
  const [isExpanded, setIsExpanded] = useState(hasActiveChild);

  const { Icon } = section;

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      sx={{
        backgroundColor: 'transparent',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.5,
          minHeight: 44,
          borderRadius: '6px',
          '& .MuiAccordionSummary-content': {
            my: 0,
            alignItems: 'center',
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {Icon ? (
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: hasActiveChild
                ? theme.palette.primary.main
                : theme.palette.common.black,
            }}
          >
            <Icon color="inherit" />
          </ListItemIcon>
        ) : null}
        <Typography
          variant="subtitle2"
          lineHeight={1.5}
          noWrap
          title={section.title}
          color={hasActiveChild ? theme.palette.primary.main : undefined}
          sx={{ minWidth: 0 }}
        >
          {section.title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pl: 2 }}>
        <Box display="flex" flexDirection="column">
          {visibleChildren.map((link) => (
            <MenuListLink
              key={link.path}
              path={link.path}
              title={link.title}
              Icon={link.Icon}
              isActive={activeChildPath === link.path}
              onClose={onLinkClick}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
